import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Quotes() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [parts, setParts] = useState([]);
  const [taskId, setTaskId] = useState("");
  const [workshopId, setWorkshopId] = useState("");
  const [labor, setLabor] = useState(0);
  const [items, setItems] = useState([]);

  const load = () => api.get("/quotes").then(({ data }) => setList(data));
  useEffect(() => {
    load();
    Promise.all([api.get("/tasks"), api.get("/workshops"), api.get("/parts")]).then(([t, w, p]) => {
      setTasks(t.data); setWorkshops(w.data); setParts(p.data);
    });
  }, []);

  const addItem = () => setItems([...items, { part_id: "", qty: 1, unit_cost: 0, name: "" }]);
  const updateItem = (i, patch) => setItems(items.map((x, k) => k === i ? { ...x, ...patch } : x));
  const rmItem = (i) => setItems(items.filter((_, k) => k !== i));

  const onPartSelect = (i, pid) => {
    const p = parts.find((x) => x.id === pid);
    updateItem(i, { part_id: pid, unit_cost: p?.unit_cost || 0, name: p?.name || "" });
  };

  const total = items.reduce((s, x) => s + Number(x.qty) * Number(x.unit_cost), 0) + Number(labor);

  const save = async () => {
    if (!taskId || !workshopId) return toast.error("Selecione tarefa e oficina");
    try {
      await api.post("/quotes", { task_id: taskId, workshop_id: workshopId, parts: items, labor_cost: Number(labor) });
      toast.success("Orçamento criado");
      setOpen(false); setTaskId(""); setWorkshopId(""); setLabor(0); setItems([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (q, status) => {
    try { await api.post(`/quotes/${q.id}/status`, { status }); toast.success("Status atualizado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="quotes-page">
      <PageHeader title="Orçamentos" subtitle="Custos de peças e mão de obra por oficina"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="gap-2" data-testid="new-quote-btn"><Plus className="w-4 h-4" />Novo Orçamento</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Novo Orçamento</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Tarefa/OS</Label>
                  <Select value={taskId} onValueChange={setTaskId}>
                    <SelectTrigger data-testid="quote-task"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.template?.title} · #{t.vehicle?.fleet_number}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Oficina</Label>
                  <Select value={workshopId} onValueChange={setWorkshopId}>
                    <SelectTrigger data-testid="quote-workshop"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{workshops.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Peças</Label>
                    <Button size="sm" variant="outline" onClick={addItem} className="gap-1" data-testid="add-quote-part"><Plus className="w-3 h-3" />Adicionar</Button>
                  </div>
                  {items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center mb-2">
                      <Select value={it.part_id} onValueChange={(v) => onPartSelect(i, v)}>
                        <SelectTrigger className="col-span-6" data-testid={`qp-${i}`}><SelectValue placeholder="Peça" /></SelectTrigger>
                        <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" placeholder="Qtd" value={it.qty} onChange={(e) => updateItem(i, { qty: Number(e.target.value) })} className="col-span-2" data-testid={`qq-${i}`} />
                      <Input type="number" placeholder="Custo" value={it.unit_cost} onChange={(e) => updateItem(i, { unit_cost: Number(e.target.value) })} className="col-span-3" />
                      <Button size="icon" variant="ghost" onClick={() => rmItem(i)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  ))}
                </div>
                <div><Label>Mão de obra (R$)</Label><Input type="number" value={labor} onChange={(e) => setLabor(e.target.value)} data-testid="quote-labor" /></div>
                <div className="text-right font-display font-bold text-xl">Total: {brl(total)}</div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-quote">Salvar Orçamento</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tarefa</TableHead><TableHead>Oficina</TableHead>
            <TableHead className="text-right">Peças</TableHead><TableHead className="text-right">M.O.</TableHead>
            <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((q) => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="text-xs">{q.task_id?.slice(0, 8)}</TableCell>
                <TableCell>{q.workshop?.name}</TableCell>
                <TableCell className="text-right font-mono">{brl(q.total_parts_cost)}</TableCell>
                <TableCell className="text-right font-mono">{brl(q.labor_cost)}</TableCell>
                <TableCell className="text-right font-mono font-bold">{brl(q.total_cost)}</TableCell>
                <TableCell><StatusBadge status={q.status} /></TableCell>
                <TableCell>
                  <Select value={q.status} onValueChange={(v) => changeStatus(q, v)}>
                    <SelectTrigger className="w-32 h-8" data-testid={`quote-status-${q.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Orçado">Orçado</SelectItem>
                      <SelectItem value="Aprovado">Aprovado</SelectItem>
                      <SelectItem value="Executado">Executado</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
